import React, { useMemo } from 'react';

import { Svg, SvgPath, SvgProps } from '@rbilabs/universal-components';

import Styles from 'styles/constants';
import { primitive } from 'styles/constants/primitives';

import { MarkerTypes } from '../types';

const MARKER_DIMENSIONS = {
  width: 51,
  height: 54,
  viewBox: '0 0 51 54',
} as const;

const STORE_FAVORITE_PATHS = {
  background:
    'M25 4.5c-8.5 0-15.3 6.9-15.3 15.3C9.7 31.3 25 48.2 25 48.2s15.3-17 15.3-28.4c0-8.4-6.9-15.3-15.3-15.3Z',
  content:
    'm25 28.6-1.3-1.1c-4.5-4-7.5-6.5-7.5-9.7 0-2.5 2.1-4.5 4.8-4.5 1.6 0 3 .6 4 1.7 1-1 2.4-1.7 3.9-1.7 2.7 0 4.8 2 4.8 4.5 0 3.2-3 5.8-7.5 9.7l-1.2 1Z',
} as const;

const SHADOW_STYLES = {
  shadowColor: Styles.shadowColor,
  shadowOffset: {
    width: 1.2,
    height: 1.2,
  },
  shadowRadius: 1.5,
  shadowOpacity: 0.8,
  elevation: 10, // for android
} as const;

const STORE_FAVORITE_MARKER_COLORS: Partial<Record<MarkerTypes, string>> = {
  [MarkerTypes.StoreFavNotAvailable]: primitive.bk.$surfacesWhite,
  [MarkerTypes.StoreFav]: primitive.bk.$bbqBrown,
} as const;

const getStoreMarkerColor = (status: MarkerTypes): string =>
  STORE_FAVORITE_MARKER_COLORS[status] || primitive.bk.$bbqBrown;

type StoreFavoriteMarkerStatus = MarkerTypes.StoreFavNotAvailable | MarkerTypes.StoreFav;

interface StoreMarkerFavoriteProps extends SvgProps {
  status: StoreFavoriteMarkerStatus;
  accessibilityLabel?: string;
  testID?: string;
}

export const StoreMarkerFavorite: React.FC<StoreMarkerFavoriteProps> = ({
  status,
  accessibilityLabel,
  testID,
  ...svgProps
}) => {
  const markerColor = useMemo(() => getStoreMarkerColor(status), [status]);

  const defaultAccessibilityLabel = useMemo(() => {
    const availabilityText =
      status === MarkerTypes.StoreFavNotAvailable ? 'unavailable' : 'available';
    return `Favorite store marker - ${availabilityText}`;
  }, [status]);

  return (
    <Svg
      width={MARKER_DIMENSIONS.width}
      height={MARKER_DIMENSIONS.height}
      viewBox={MARKER_DIMENSIONS.viewBox}
      fill="none"
      style={SHADOW_STYLES}
      accessible={true}
      accessibilityLabel={accessibilityLabel || defaultAccessibilityLabel}
      testID={testID}
      {...svgProps}
    >
      <SvgPath d={STORE_FAVORITE_PATHS.background} fill={markerColor} />
      <SvgPath d={STORE_FAVORITE_PATHS.content} fill="#fff" />
    </Svg>
  );
};

export const getStoreFavoriteMarkerSvg = (
  status: MarkerTypes.StoreFavNotAvailable | MarkerTypes.StoreFav
): string => {
  const markerColor = getStoreMarkerColor(status);

  return `<svg width="${MARKER_DIMENSIONS.width}" height="${MARKER_DIMENSIONS.height}" viewBox="${MARKER_DIMENSIONS.viewBox}" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="${STORE_FAVORITE_PATHS.background}" fill="${markerColor}" />
    <path d="${STORE_FAVORITE_PATHS.content}" fill="#fff" />
  </svg>`;
};
